import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const REOPEN_AFTER_MS = 10 * 60 * 1000; // flash again every 10 minutes

function Countdown({ endsOn }) {
  const target = new Date(`${endsOn}T23:59:59+05:30`).getTime();
  const [left, setLeft] = useState(target - Date.now());

  useEffect(() => {
    const t = setInterval(() => setLeft(target - Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);

  if (left <= 0) return null;
  const days = Math.floor(left / 86400000);
  const hours = Math.floor((left % 86400000) / 3600000);
  const mins = Math.floor((left % 3600000) / 60000);
  const secs = Math.floor((left % 60000) / 1000);
  const blocks = days > 0 ? [[days, "days"], [hours, "hrs"], [mins, "min"]] : [[hours, "hrs"], [mins, "min"], [secs, "sec"]];

  return (
    <div className="mt-4" data-testid="offer-popup-countdown">
      <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#F7D98B]">Offer ends in</p>
      <div className="mt-1.5 flex items-center justify-center gap-2">
        {blocks.map(([value, label]) => (
          <div key={label} className="min-w-[58px] rounded-xl border border-[#F5C45A]/50 bg-black/25 px-2 py-1.5">
            <div className="font-display text-xl font-extrabold leading-none text-[#FFE9B0]">{String(value).padStart(2, "0")}</div>
            <div className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.2em] text-[#F5C45A]">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function OfferPopup() {
  const { drawerOpen } = useCart();
  const cartOpen = useRef(drawerOpen);
  cartOpen.current = drawerOpen;
  const [offers, setOffers] = useState([]);
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let live = true;
    let timer;
    api.get("/offers")
      .then(({ data }) => {
        if (!live || !data.length) return;
        setOffers(data);
        timer = setTimeout(() => { if (!cartOpen.current) setOpen(true); }, 900);
      })
      .catch(() => {});
    return () => { live = false; clearTimeout(timer); };
  }, []);

  useEffect(() => { if (drawerOpen) setOpen(false); }, [drawerOpen]);

  // show it again every 10 minutes for as long as they stay on the site
  useEffect(() => {
    if (!offers.length) return;
    const t = setInterval(() => {
      if (cartOpen.current) return;
      setIndex(0);
      setOpen(true);
    }, REOPEN_AFTER_MS);
    return () => clearInterval(t);
  }, [offers.length]);

  useEffect(() => {
    if (!open || offers.length < 2) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % offers.length), 5000);
    return () => clearInterval(t);
  }, [open, offers.length, index]);

  const dismiss = () => setOpen(false);

  const openOffer = (offer) => {
    dismiss();
    if (offer.deal_type === "bundle") {
      window.dispatchEvent(new CustomEvent("vs:combo", { detail: { offerId: offer.id } }));
      return;
    }
    if (offer.link_type !== "none" && offer.link_value) {
      window.dispatchEvent(new CustomEvent("vs:nav", { detail: { label: offer.link_value, href: offer.link_value } }));
    } else {
      document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const offer = offers[index];

  return (
    <AnimatePresence>
      {open && offer && !drawerOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="offer-popup"
          className="fixed inset-0 z-[90] flex items-center justify-center bg-[#1A0406]/90 p-4 backdrop-blur-sm"
          onClick={dismiss}
        >
          <motion.div
            initial={{ scale: 0.88, y: 28, rotate: -1 }}
            animate={{ scale: 1, y: 0, rotate: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md overflow-hidden rounded-[28px] border-2 border-[#F5C45A] bg-gradient-to-br from-[#8A0F1E] via-[#B1121F] to-[#5C0712] shadow-[0_0_60px_-10px_rgba(245,196,90,0.55)]"
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#F5C45A]/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-12 h-48 w-48 rounded-full bg-[#FF8A3D]/20 blur-3xl" />

            <button
              data-testid="offer-popup-close"
              onClick={dismiss}
              className="absolute right-3 top-3 z-10 rounded-full border border-[#F5C45A]/50 bg-black/30 p-2 text-[#F5C45A] transition-colors hover:bg-[#F5C45A] hover:text-[#5C0712]"
            >
              <X size={15} />
            </button>

            {offer.image && (
              <div className="relative h-52 w-full overflow-hidden border-b-2 border-[#F5C45A]/60">
                <img src={offer.image} alt={offer.title} data-testid="offer-popup-image" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#5C0712] via-transparent to-transparent" />
              </div>
            )}

            <div className="relative px-7 pb-7 pt-6 text-center">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-[#F5C45A]/60 bg-[#F5C45A]/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#F7D98B]">
                <Sparkles size={11} /> Festive offer
              </div>
              <h3 data-testid="offer-popup-title" className="mt-3 font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-[#FFE9B0] drop-shadow-[0_2px_0_rgba(0,0,0,0.35)]">
                {offer.title}
              </h3>
              {offer.message && (
                <p data-testid="offer-popup-message" className="mt-2.5 text-sm leading-relaxed text-[#FFD9DC]">
                  {offer.message}
                </p>
              )}
              {offer.discount_percent > 0 && (
                <p className="mt-2 font-display text-lg font-extrabold uppercase tracking-tight text-[#FFE9B0]" data-testid="offer-popup-discount">
                  Flat {Math.round(offer.discount_percent)}% off
                </p>
              )}
              {offer.ends_on && <Countdown endsOn={offer.ends_on} />}

              <button
                data-testid="offer-popup-cta"
                onClick={() => openOffer(offer)}
                className="mt-5 w-full rounded-full bg-gradient-to-r from-[#FFD66B] via-[#F5C45A] to-[#E0A52E] py-3.5 text-sm font-extrabold uppercase tracking-[0.15em] text-[#5C0712] transition-transform duration-300 hover:scale-[1.03] active:scale-95"
              >
                {offer.deal_type === "bundle" ? "View combo" : offer.cta_label || "Shop now"}
              </button>

              {offers.length > 1 && (
                <div className="mt-4 flex items-center justify-center gap-3">
                  <button
                    data-testid="offer-popup-prev"
                    onClick={() => setIndex((i) => (i - 1 + offers.length) % offers.length)}
                    className="rounded-full border border-[#F5C45A]/40 p-1.5 text-[#F5C45A] hover:bg-[#F5C45A]/15"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <div className="flex gap-1.5" data-testid="offer-popup-dots">
                    {offers.map((o, i) => (
                      <button
                        key={o.id}
                        onClick={() => setIndex(i)}
                        data-testid={`offer-popup-dot-${i}`}
                        className={`h-1.5 rounded-full transition-all ${i === index ? "w-6 bg-[#F5C45A]" : "w-1.5 bg-[#F5C45A]/35"}`}
                      />
                    ))}
                  </div>
                  <button
                    data-testid="offer-popup-next"
                    onClick={() => setIndex((i) => (i + 1) % offers.length)}
                    className="rounded-full border border-[#F5C45A]/40 p-1.5 text-[#F5C45A] hover:bg-[#F5C45A]/15"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
